import type { Infer } from 'superstruct';
import {
	any,
	array,
	assign,
	nullable,
	number,
	object,
	optional,
	record,
	string,
	union,
} from 'superstruct';

import { ListResourceDto, PaginationQuery } from '@adflow/shared/util-data';
import { adflowApiSchema } from '@adflow/shared/util-route-schema';
import {
	parsableBoolean,
	parsableInteger,
} from '@adflow/shared/util-superstruct';

import { ViewGoogleAdsAccountDto } from './google-ads-account.dto';
import { GoogleAdsAudienceSegment } from './google-ads-audience-segment.dto';
import { ViewGoogleAdsConversionGoalDto } from './google-ads-conversion-goal.dto';
import { ViewGoogleAdsTopicDto } from './google-ads-group.dto';
import { GoogleAdsMerchantCenterFeed } from './google-ads-merchant-center.dto';
import {
	AddGoogleAdsTemplateDto,
	ViewGoogleAdsTemplateDto,
} from './google-ads-template.dto';
import { AddKeywordDto, EditKeywordDto, ViewKeywordDto } from './keyword.dto';
import { ListKeywordSort } from './list-keyword-query.dto';
import { ListPromotionAssetSort } from './list-promotion-asset-query.dto';
import {
	AddPromotionAssetDto,
	EditPromotionAssetDto,
	ViewPromotionAssetDto,
} from './promotion-asset.dto';

const p = adflowApiSchema.prefix('google-ads');

export const AddGoogleAdsTemplate = p.post('google-ads', {
	req: object({
		body: AddGoogleAdsTemplateDto,
		query: object({ duplicated: optional(parsableBoolean()) }),
	}),
	res: ViewGoogleAdsTemplateDto,
});

export const ArchiveGoogleAdsTemplate = p.post(
	'templates/:templateId/archive',
	{
		req: object({
			params: object({ templateId: parsableInteger() }),
		}),
		res: ViewGoogleAdsTemplateDto,
	}
);

export const GetGoogleAdsTemplateEnums = p.post('enums', {
	req: object({ body: object({ avbIds: optional(array(string())) }) }),
	res: object({
		campaign: object({
			status: array(string()),
			type: array(string()),
			mobileAppCategories: array(object({ id: number(), name: string() })),
			language: record(string(), string()),
			biddingStrategyType: array(string()),
			network: record(string(), string()),
			locationTargeting: record(string(), string()),
		}),
		group: object({
			status: array(string()),
			maxCPC: array(string()),
			audience: array(
				object({
					id: number(),
					category: string(),
					taxonomyType: string(),
					availabilities: array(string()),
				})
			),
			keywordLists: array(
				object({
					id: number(),
					name: string(),
					type: string(),
					list: array(string()),
				})
			),
			userLists: array(object({ name: string(), type: string() })),
			topics: array(ViewGoogleAdsTopicDto),
		}),
		ad: object({
			status: array(string()),
			type: array(string()),
			callToAction: array(string()),
		}),
		promotionAsset: object({
			occasion: array(string()),
			details: array(string()),
			promoType: array(string()),
			language: array(string()),
			currency: array(string()),
		}),
		structuredSnippet: object({
			headers: array(string()),
		}),
	}),
});

export const GetGoogleMyBusinessLocations = p.get('locations', {
	req: object({}),
	res: array(any()),
});

export const GetGoogleAdsTemplateAudienceSegments = p.get('audience-segments', {
	req: object({ query: object({ avbIds: optional(array(string())) }) }),
	res: array(GoogleAdsAudienceSegment),
});

export const GetGoogleAdsTemplateMerchantCenterFeeds = p.get(
	'merchant-center-feeds',
	{
		req: object({ query: object({ avbIds: optional(array(string())) }) }),
		res: array(GoogleAdsMerchantCenterFeed),
	}
);

// export const GetGoogleAdsAccountsQuery = object({
// 	search: string(),
// });

// export type GetGoogleAdsAccountsQuery = Infer<
// 	typeof GetGoogleAdsAccountsQuery
// >;

export const GetGoogleAdsAccounts = p.get('accounts', {
	req: object({}),
	res: array(ViewGoogleAdsAccountDto),
});

export const DeleteGoogleAdsTemplates = p.delete('templates/delete', {
	req: object({
		body: object({ templateIds: array(parsableInteger()) }),
	}),
	res: object({}),
});

export const DeleteGoogleAdsTemplate = p.delete('templates/:templateId', {
	req: object({
		params: object({ templateId: parsableInteger() }),
	}),
	res: number(),
});

export const UploadGoogleAdsTemplate = p.post('templates/:templateId/upload', {
	req: object({
		params: object({ templateId: parsableInteger() }),
	}),
	res: any(),
});

export const GetGoogleAdsTemplateUploadProgress = p.get(
	'templates/:templateId/upload-progress',
	{
		req: object({
			params: object({ templateId: parsableInteger() }),
		}),
		res: nullable(
			object({
				complete: number(),
				total: number(),
			})
		),
	}
);

const DuplicateErrorDto = object({
	error: string(),
});

export const ListKeywordsQuery = assign(
	PaginationQuery,
	ListKeywordSort,
	object({ search: optional(string()) })
);

export type ListKeywordsQuery = Infer<typeof ListKeywordsQuery>;

export const ListKeywords = p.get('keywords', {
	req: object({
		query: ListKeywordsQuery,
	}),
	res: ListResourceDto(ViewKeywordDto),
});

export const AddKeyword = p.post('keywords', {
	req: object({
		body: AddKeywordDto,
	}),
	res: union([ViewKeywordDto, DuplicateErrorDto]),
});

export const EditKeyword = p.post('keywords/:keywordId', {
	req: object({
		params: object({ keywordId: parsableInteger() }),
		body: EditKeywordDto,
	}),
	res: union([ViewKeywordDto, DuplicateErrorDto]),
});

export const DeleteKeyword = p.delete('keywords/:keywordId', {
	req: object({
		params: object({ keywordId: parsableInteger() }),
	}),
	res: number(),
});

export const ListPromotionAssetsQuery = assign(
	PaginationQuery,
	ListPromotionAssetSort,
	object({ search: optional(string()) })
);

export type ListPromotionAssetsQuery = Infer<typeof ListPromotionAssetsQuery>;

export const ListPromotionAssets = p.get('promotion-assets', {
	req: object({
		query: ListPromotionAssetsQuery,
	}),
	res: ListResourceDto(ViewPromotionAssetDto),
});

export const GetPromotionAsset = p.get('promotion-assets/:promotionAssetId', {
	req: object({
		params: object({ promotionAssetId: parsableInteger() }),
	}),
	res: ViewPromotionAssetDto,
});

export const AddPromotionAsset = p.post('promotion-assets', {
	req: object({
		body: AddPromotionAssetDto,
	}),
	res: union([ViewPromotionAssetDto, DuplicateErrorDto]),
});

export const EditPromotionAsset = p.post('promotion-assets/:promotionAssetId', {
	req: object({
		params: object({ promotionAssetId: parsableInteger() }),
		body: EditPromotionAssetDto,
	}),
	res: union([ViewPromotionAssetDto, DuplicateErrorDto]),
});

export const DeletePromotionAsset = p.delete(
	'promotion-assets/:promotionAssetId',
	{
		req: object({
			params: object({ promotionAssetId: parsableInteger() }),
		}),
		res: number(),
	}
);

export const ApplyPromotionAssetToCampaigns = p.post(
	'promotion-assets/:promotionAssetId/apply-to-campaigns',
	{
		req: object({
			params: object({ promotionAssetId: parsableInteger() }),
			body: object({ campaignTemplateIds: array(parsableInteger()) }),
		}),
		res: object({ success: parsableBoolean() }),
	}
);

export const ApplyPromotionAssetToAdAccounts = p.post(
	'promotion-assets/:promotionAssetId/apply-to-ad-accounts',
	{
		req: object({
			params: object({ promotionAssetId: parsableInteger() }),
			body: object({ adAccountAvbIds: array(string()) }),
		}),
		res: object({ success: parsableBoolean() }),
	}
);

export const GetGoogleAdsConversionGoals = p.get('conversion-goals', {
	req: object({
		query: object({ avbIds: optional(array(string())) }),
	}),
	res: array(ViewGoogleAdsConversionGoalDto),
});

export const SyncGoogleAdsAccounts = p.post('sync-accounts', {
	req: object({}),
	res: object({}),
});
